import React from "react";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
  TimelineDot,
} from "@mui/lab";
import { Typography, Tooltip } from "@mui/material";
import { localDateTime, localTime } from "./localDateTime";

export interface TimelineEntry {
  at: Date;
  title: string;
  /** status semántico: completed | failed | dead_letter | waiting | queued | processing | gate | info */
  status: string;
  detail?: string;
}

const DOT_COLOR: Record<string, "success" | "error" | "warning" | "primary" | "info" | "grey"> = {
  completed: "success",
  failed: "error",
  dead_letter: "error",
  waiting: "warning",
  gate: "warning",
  queued: "info",
  processing: "primary",
  info: "grey",
};

/**
 * Cronología del circuito (qué sucedió, en qué orden): timeline vertical con
 * hora a la izquierda, punto semántico y título+detalle. Ascendente (la
 * historia se lee de arriba hacia abajo).
 */
export const CircuitTimeline = ({ entries }: { entries: TimelineEntry[] }) => {
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        — sin actividad registrada —
      </Typography>
    );
  }
  const sorted = [...entries].sort((a, b) => a.at.getTime() - b.at.getTime());
  return (
    <Timeline
      sx={{
        m: 0,
        p: 0,
        "& .MuiTimelineOppositeContent-root": { flex: 0.18, minWidth: 92 },
      }}
    >
      {sorted.map((e, i) => (
        <TimelineItem key={i}>
          <TimelineOppositeContent>
            <Tooltip title={localDateTime(e.at)}>
              <Typography variant="body2" color="text.secondary">
                {localTime(e.at)}
              </Typography>
            </Tooltip>
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot color={DOT_COLOR[e.status] ?? "grey"} variant="filled" />
            {i < sorted.length - 1 && <TimelineConnector />}
          </TimelineSeparator>
          <TimelineContent>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {e.title}
            </Typography>
            {e.detail && (
              <Typography variant="body2" color={e.status === "dead_letter" || e.status === "failed" ? "error" : "text.secondary"}>
                {e.detail}
              </Typography>
            )}
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  );
};
